import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

import styles from "../../../styles/patient-page-modules/orderDeviceModal.module.css";
import Spinner from "../../../components/spinner";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { orderNewDevice } from "../../../services/patient/devices";
import {
  createBillingCode,
  updateBillingCode,
} from "../../../services/case-manager/billing/setting";
import { getStates } from "../../../services/location";
import toast from "react-hot-toast";
import Button from "../../button-v1";
import { formatNumber } from "../../../utils/helper";
const schema = {
  category: "",
  price: 0.0,
  description: "",
  code: "",
  bill_once_per_month: true,
  only_once: false,
};

const validationSchema = z.object({
  category: z.enum(["CCM", "RPM"], {
    message: "category is required",
  }),

  price: z
    .number({
      required_error: "price is required",
      invalid_type_error: "price must be a number",
    })
    .positive("price must be a positive number")
    .refine(
      (val) => {
        const formatted = formatNumber("decimal", val);
        return formatted.toString();
      },
      {
        message: "Price must have exactly two decimal places",
      }
    ),
  only_once: z.boolean().default(false),

  bill_once_per_month: z.boolean().default(false),
  code: z.string().min(1, "code is required"),
  description: z
    .string()
    .max(500, "description can't be longer than 500 characters")
    .optional(),
});

const OrderDeviceModal = ({
  isOpen,
  onClose,
  billingCodeId,
  setBillingCodeId,
  billingCode,
}) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: schema,
    resolver: zodResolver(validationSchema),
  });

  const category = watch("category");

  const {
    mutate: updateBillingCodeMutation,
    isLoading: isUpdateBillingCodeMutationLoading,
  } = useMutation((data) => updateBillingCode(billingCodeId, data), {
    onSuccess: () => {
      toast.success("Billing code update successfully");
      queryClient.invalidateQueries(["billing-codes"]);
      setBillingCodeId(null);
      onClose();
    },
    onError: () => {
      toast.error("Could not update billing code");
    },
  });

  const {
    mutate: createBillingCodeMutation,
    isLoading: isCreateBillingCodeLoading,
  } = useMutation((data) => createBillingCode(data), {
    onSuccess: () => {
      toast.success("Billing code added successfully");
      queryClient.invalidateQueries(["billing-codes"]);

      onClose();
    },
    onError: () => {
      toast.error("Could not add billing code");
    },
  });

  useEffect(() => {
    if (!billingCodeId || !billingCode) return;
    setValue("category", billingCode.category);
    setValue("code", billingCode.code);
    setValue("price", billingCode.price);
    setValue("bill_once_per_month", billingCode.bill_once_per_month);
    setValue("only_once", billingCode.only_once);
    setValue("description", billingCode.description);
  }, [billingCodeId, billingCode]);

  const submitHandler = (values) => {
    if (billingCodeId) {
      updateBillingCodeMutation(values);
    } else {
      createBillingCodeMutation(values);
    }
  };

  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {billingCodeId ? "Update Billing Code" : "Add Billing Code"}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form className={styles.input_wrapper}>
            <div className={styles.input_group}>
              <div className={styles.input_field}>
                <label>Code</label>
                <input
                  type="text"
                  name="code"
                  placeholder="billing code"
                  {...register("code")}
                />
                <span className="error-message">{errors?.code?.message}</span>
              </div>

              <div className={styles.input_field}>
                <label>Price</label>
                <input
                  type="Number"
                  step="0.01"
                  name="price"
                  {...register("price", { valueAsNumber: true })}
                />
                <span className="error-message">{errors?.price?.message}</span>
              </div>
            </div>
            {/* <div className={styles.input_group}>
              <div className={styles.input_field}>
                <div className={styles.input_field}>
                  <label>Code Type</label>
                  <Select
                    onChange={(value) => setValue("category", value)}
                    value={category}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type">
                        {category ?? "Select code type"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="RPM">RPM</SelectItem>
                      <SelectItem value="CCM">CCM</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="error-message">
                    {errors?.category?.message}
                  </span>
                </div>
              </div>
            </div> */}
            {/* <div className={styles.input_group_2}>
              <div className={styles.input_field}>
                <label>Once Per Month Billing</label>

                <label className={`${styles.switch}`}>
                  <input
                    type="checkbox"
                    name="bill_once_per_month"
                    className={`${styles.input} ${styles.checkbox}`}
                    // checked={formData.require_signature}
                    // onChange={handleCheckBoxChange}
                    {...register("bill_once_per_month")}
                  />
                  <span className={styles.slider}></span>
                </label>
              </div>
            </div> */}

            {/* <div className={styles.input_group_2}>
              <div className={styles.input_field}>
                <label>One Time Billing</label>

                <label className={`${styles.switch}`}>
                  <input
                    type="checkbox"
                    name="only_once"
                    className={`${styles.input} ${styles.checkbox}`}
                    // checked={formData.require_signature}
                    // onChange={handleCheckBoxChange}
                    {...register("only_once")}
                  />
                  <span className={styles.slider}></span>
                </label>
              </div>
            </div> */}

            <div className={styles.input_group}>
              <div className={styles.input_field}>
                <label>Description</label>
                <textarea
                  type="text"
                  name="description"
                  {...register("description")}
                />
              </div>
              {/* <div className={styles.input_field}></div> */}
            </div>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose()}>
            Cancel
          </Button>
          {billingCodeId ? (
            <Button
              onClick={handleSubmit(submitHandler)}
              loading={isUpdateBillingCodeMutationLoading}
              disabled={isUpdateBillingCodeMutationLoading}
            >
              Update
            </Button>
          ) : (
            <Button
              onClick={handleSubmit(submitHandler)}
              loading={isCreateBillingCodeLoading}
              disabled={isCreateBillingCodeLoading}
            >
              Create
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDeviceModal;
